import { Box, type SxProps, type Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import DomainTab from "./DomainTab.component";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";

type KnowledgeArea = { id: number, name: string };
type KnowledgeDomain = { id: number, name: string, knowledge_area_id?: number };
type Capability = { id: number, name: string, type?: string };

type KnowledgeAndCapabilitiesProps = {
  knowledge?: {
    knowledge_areas?: KnowledgeArea[]
    knowledge_domains?: KnowledgeDomain[]
  } | null
  capabilities?: Capability[] | null
  sx?: SxProps<Theme>
};


export function KnowledgeAndCapabilities({ knowledge, capabilities, sx }: KnowledgeAndCapabilitiesProps) {
  const { t } = useTranslation();
  const { customLogicFe } = getEnvironmentVariables();
  const hideKnowledgeArea = checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hideKnowledgeArea" });

  const areas = hideKnowledgeArea ? [] : (knowledge?.knowledge_areas ?? []);
  const domains = knowledge?.knowledge_domains ?? [];
  const allCapabilities = capabilities ?? [];
  const areaIds = new Set(areas.map((area) => area.id));
  const orphanDomains = domains.filter((domain) =>
    domain.knowledge_area_id == null || !areaIds.has(domain.knowledge_area_id)
  );

  const capabilityRows: { key: string, headLabel?: string, items: Capability[] }[] = [
    {
      headLabel: t("capability_type_professional"),
      items: allCapabilities.filter((c) => c.type === "PROFESSIONAL"),
      key: "professional"
    },
    {
      headLabel: t("capability_type_manager"),
      items: allCapabilities.filter((c) => c.type === "MANAGER"),
      key: "manager"
    },
    {
      items: allCapabilities.filter((c) => c.type !== "PROFESSIONAL" && c.type !== "MANAGER"),
      key: "other"
    }
  ].filter((row) => row.items.length > 0);

  const hasRows = areas.length > 0 || orphanDomains.length > 0 || capabilityRows.length > 0;
  if (!hasRows) return null;

  return (
    <Box sx={ sx }>
      { areas.map((area) => (
        <DomainTab
          key={ `knowledge-area-${area.id}` }
          headLabel={ area.name }
          array={ domains.filter((domain) => domain.knowledge_area_id === area.id) }
        />
      )) }
      { orphanDomains.length > 0 && (
        <DomainTab array={ orphanDomains } />
      ) }
      { capabilityRows.map((row) => (
        <DomainTab
          key={ `capabilities-${row.key}` }
          headLabel={ row.headLabel }
          array={ row.items }
        />
      )) }
    </Box>
  );
}
